// Editable filter chips for the "הוספת נכסים" discovery surface (mock s18).
// Each chip opens a small popover editor; every change re-runs the search instantly
// through the parent's onChange. New components by design — the legacy pickers
// (PriceRangePicker & co.) are page-scale controls tied to the god-context.
//
// The row is also the AT-A-GLANCE SUMMARY of everything active, including the advanced
// filters that only the full drawer (MatchesFiltersDrawer) can set: those render as
// read-only-but-clearable summary chips after the editable ones, so opening the drawer
// is never required just to see — or undo — what is filtering the results.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Button,
  Popover,
  Divider,
  Tooltip,
  Checkbox,
  debounce,
  TextField,
  Typography,
  FormControlLabel,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@material-ui/core';
import { alpha, useTheme } from '@material-ui/core/styles';
import { Close, Tune, RestartAlt, KeyboardArrowDown } from '@material-ui/icons';
import useWebotServer from 'hooks/useWebotServer';
import CurrencyInput from 'components/__haim/Common/CurrencyInput';
import { PropertyTypeCheckboxList } from 'components/__haim/Common/PropertyTypeCheckboxList';
import RoomRangePicker from 'components/_dashboard/listingsMatch/RoomRangePicker';
import FloorRangePicker from 'components/_dashboard/listingsMatch/FloorRangePicker';
import SizeRangePicker from 'components/_dashboard/listingsMatch/SizeRangePicker';
import { getMinMaxSquareMeterString } from 'utils/Haim/utils';
import { propertyTypes as allPropertyTypes } from '../../../../../../@types/sharedSchema';
import type {
  Room,
  Floor,
  ProcessedListingAddress,
  GenericPropertyDetails
} from '../../../../../../@types/sharedSchema';
import { buildSelectionChips } from 'utils/locations/locationGroups';
import { formatBidiRange, isolateLtr } from 'utils/bidi';
import { locationSuggestionToAddress } from './matchesSearch';
import { formatPriceShort, AMENITY_LABELS } from './matchesDisplay';
import { VISIBLE_BOOLEAN_FILTER_KEYS } from './contactMatchFilters';
import type { MatchFilters } from './contactMatchFilters';
import {
  PROTECTED_SPACE_GROUP_HINT,
  PROTECTED_SPACE_GROUP_LABEL,
  PROTECTED_SPACE_KEYS,
  hasAnyProtectedSpace
} from 'utils/contacts/amenityLabels';
import { advancedFilterSummaries } from './matchFiltersAdapter';

/** The filterable amenities that are NOT in the מרחב מוגן group — the plain ANDed ones. */
const STANDALONE_FILTER_KEYS = VISIBLE_BOOLEAN_FILTER_KEYS.filter((key) => !PROTECTED_SPACE_KEYS.includes(key));

type ChipId = 'price' | 'rooms' | 'size' | 'locations' | 'types' | 'more';

/**
 * Marks a budget that has been widened to admit unpriced listings. It rides on the
 * price chip rather than becoming a chip of its own because it is a property OF the
 * budget — on its own it filters nothing, and a chip that clears to no visible effect
 * reads as broken.
 */
const INCLUDE_WITHOUT_PRICE_CHIP_SUFFIX = '+ ללא מחיר';

type MatchFilterChipsProps = {
  filters: MatchFilters;
  onChange: (patch: Partial<MatchFilters>) => void;
  /** Opens the full filters drawer (MatchesFiltersDrawer). */
  onOpenAdvanced: () => void;
  /**
   * True when the active filters differ from what the contact's search profile defines.
   * The reset control is rendered only then — on a freshly-seeded search it would be
   * dead chrome that resets to the state already on screen.
   */
  canResetToContact: boolean;
  /** Restores exactly the contact-derived filters (chips AND drawer filters). */
  onResetToContact: () => void;
};

const locationLabel = (location: ProcessedListingAddress): string =>
  location.street
    ? `${location.street}${location.city ? `, ${location.city}` : ''}`
    : location.neighborhood || location.city || '';

/**
 * The bounds are wrapped in LTR isolates (`formatBidiRange` / `isolateLtr`) rather
 * than concatenated raw. A range like "₪1.6M–₪2.5M" holds no Hebrew at all, so the
 * bidi algorithm laid the pair out left-to-right off the Latin "M" and put the LOW
 * bound on the LEFT — backwards beside every other chip on a Hebrew page. Isolating
 * each bound keeps its own digits left-to-right while the PAIR follows the page.
 */
function priceChipLabel(filters: MatchFilters): string {
  const { minPrice, maxPrice, includeListingsWithoutPrice } = filters;
  const suffix = includeListingsWithoutPrice ? ` ${INCLUDE_WITHOUT_PRICE_CHIP_SUFFIX}` : '';
  if (minPrice && maxPrice) {
    return `${formatBidiRange(formatPriceShort(minPrice), formatPriceShort(maxPrice), '–')}${suffix}`;
  }
  if (maxPrice) return `עד ${isolateLtr(formatPriceShort(maxPrice))}${suffix}`;
  if (minPrice) return `מ־${isolateLtr(formatPriceShort(minPrice))}${suffix}`;
  return 'תקציב';
}

function roomsChipLabel(filters: MatchFilters): string {
  const { minRooms, maxRooms } = filters;
  if (minRooms !== undefined && maxRooms !== undefined) return `${minRooms}–${maxRooms} חד׳`;
  if (minRooms !== undefined) return `${minRooms}+ חד׳`;
  if (maxRooms !== undefined) return `עד ${maxRooms} חד׳`;
  return 'חדרים';
}

/**
 * Built area gets a chip of its OWN, beside rooms, rather than a line inside "+ סינון": they are
 * the two ways a customer says how big, and the field went unused for two months partly because
 * nothing on a working surface showed it (0.02% of contacts stated one, measured 2026-10-11).
 */
function sizeChipLabel(filters: MatchFilters): string {
  return getMinMaxSquareMeterString(filters.minSquareMeter, filters.maxSquareMeter) || 'שטח';
}

/** Stands in for a group with no emoji of its own — a group is a stack of areas. */
const GROUP_LABEL_ICON = '🗂️';

/**
 * The chip names what the filter IS, and a picked location group is ONE thing the
 * realtor chose — not the twelve areas it expanded into. Listing its first two
 * members and "+10" reads as ten unrelated leftovers and drops the word they
 * picked it by, so `buildSelectionChips` collapses each group's members back into
 * a single entry first: "first two + N" then counts chips, not locations.
 *
 * Deliberately no live `groups` argument, so no Firestore listener: this surface
 * neither manages nor picks groups, and the only thing a listener could add is a
 * rename made elsewhere in the same session. It renders the `fromGroupName`
 * stamped at pick time — which is also the only name that survives the group
 * being deleted. That is likewise why the emoji falls back: `buildSelectionChips`
 * reads it off the live group, so without one there is nothing to read.
 *
 * Most selections here carry no stamp at all: a saved contact's areas are
 * persisted stripped (`toPersistedLocations`), so this is about a selection still
 * in flight rather than about seeded filters.
 */
function locationsChipLabel(filters: MatchFilters): string {
  const chips = buildSelectionChips(filters.locations ?? []);
  if (chips.length === 0) return 'אזורים';
  const shown = chips
    .slice(0, 2)
    .map((chip) =>
      chip.kind === 'group'
        ? `${chip.emoji ?? GROUP_LABEL_ICON} ${chip.name}`.trim()
        : locationLabel(chip.location)
    )
    .filter(Boolean)
    .join(' · ');
  const extra = chips.length - 2;
  return extra > 0 ? `${shown} +${extra}` : shown;
}

function typesChipLabel(filters: MatchFilters): string {
  const types = filters.propertyTypes ?? [];
  if (types.length === 0) return 'סוג נכס';
  return types.length === 1 ? types[0] : `${types[0]} +${types.length - 1}`;
}

function moreActiveCount(filters: MatchFilters): number {
  let count = 0;
  if (filters.minFloors !== undefined || filters.maxFloors !== undefined) count += 1;
  count += Object.values(filters.booleanProps ?? {}).filter(Boolean).length;
  return count;
}

function FilterChip({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: (anchor: HTMLElement) => void;
}): JSX.Element {
  const theme = useTheme();
  return (
    <Button
      size="small"
      onClick={(e) => onClick(e.currentTarget)}
      endIcon={<KeyboardArrowDown sx={{ fontSize: '16px !important' }} />}
      sx={{
        borderRadius: 999,
        px: 1.5,
        py: 0.25,
        minHeight: 30,
        typography: 'caption',
        fontWeight: active ? 600 : 500,
        color: active ? 'primary.dark' : 'text.secondary',
        bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
        border: `1px solid ${
          active ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider
        }`,
        '&:hover': {
          bgcolor: active
            ? alpha(theme.palette.primary.main, 0.16)
            : alpha(theme.palette.grey[500], 0.08)
        }
      }}
    >
      {label}
    </Button>
  );
}

/** Read-only summary of one drawer-set filter, with a one-tap clear. */
function SummaryChip({ label, onClear }: { label: string; onClear: () => void }): JSX.Element {
  const theme = useTheme();
  return (
    <Button
      size="small"
      onClick={onClear}
      endIcon={<Close sx={{ fontSize: '14px !important' }} />}
      title="הסרת הסינון"
      sx={{
        borderRadius: 999,
        px: 1.5,
        py: 0.25,
        minHeight: 30,
        typography: 'caption',
        fontWeight: 600,
        color: 'primary.dark',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        '&:hover': {
          color: 'error.main',
          borderColor: 'error.main',
          bgcolor: alpha(theme.palette.error.main, 0.08)
        }
      }}
    >
      {label}
    </Button>
  );
}

export default function MatchFilterChips({
  filters,
  onChange,
  onOpenAdvanced,
  canResetToContact,
  onResetToContact
}: MatchFilterChipsProps): JSX.Element {
  const theme = useTheme();
  const [openChip, setOpenChip] = useState<ChipId | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const advancedSummaries = advancedFilterSummaries(filters);

  const openPopover = (chip: ChipId) => (anchor: HTMLElement) => {
    setOpenChip(chip);
    setAnchorEl(anchor);
  };
  const closePopover = (): void => {
    setOpenChip(null);
    setAnchorEl(null);
  };

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
      <FilterChip
        label={priceChipLabel(filters)}
        active={Boolean(filters.minPrice || filters.maxPrice)}
        onClick={openPopover('price')}
      />
      <FilterChip
        label={roomsChipLabel(filters)}
        active={filters.minRooms !== undefined || filters.maxRooms !== undefined}
        onClick={openPopover('rooms')}
      />
      <FilterChip
        label={sizeChipLabel(filters)}
        active={filters.minSquareMeter !== undefined || filters.maxSquareMeter !== undefined}
        onClick={openPopover('size')}
      />
      <FilterChip
        label={locationsChipLabel(filters)}
        active={(filters.locations ?? []).length > 0}
        onClick={openPopover('locations')}
      />
      <FilterChip
        label={typesChipLabel(filters)}
        active={(filters.propertyTypes ?? []).length > 0}
        onClick={openPopover('types')}
      />
      <FilterChip
        label={moreActiveCount(filters) > 0 ? `+ סינון (${moreActiveCount(filters)})` : '+ סינון'}
        active={moreActiveCount(filters) > 0}
        onClick={openPopover('more')}
      />

      {/* Everything the drawer set, summarised and individually clearable. */}
      {advancedSummaries.map((summary) => (
        <SummaryChip
          key={summary.id}
          label={summary.label}
          onClear={() => onChange(summary.clearPatch)}
        />
      ))}

      {/* The full filter set — the same panel the listings page uses. */}
      <Button
        size="small"
        onClick={onOpenAdvanced}
        startIcon={<Tune sx={{ fontSize: '16px !important' }} />}
        sx={{
          borderRadius: 999,
          px: 1.5,
          py: 0.25,
          minHeight: 30,
          typography: 'caption',
          fontWeight: 500,
          color: 'text.secondary',
          border: `1px dashed ${theme.palette.divider}`,
          '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.08) }
        }}
      >
        {advancedSummaries.length > 0 ? `כל המסננים (${advancedSummaries.length})` : 'כל המסננים'}
      </Button>

      {/* Restores the CONTACT's filters — deliberately not a "clear all": it puts the
          contact's saved search profile back, which is usually a filtered state. */}
      {canResetToContact && (
        <Tooltip title="החזרת הסינון למה שמוגדר בפרופיל החיפוש של הלקוח">
          <Button
            size="small"
            onClick={onResetToContact}
            startIcon={<RestartAlt sx={{ fontSize: '16px !important' }} />}
            sx={{
              borderRadius: 999,
              px: 1.5,
              py: 0.25,
              minHeight: 30,
              typography: 'caption',
              fontWeight: 500,
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) }
            }}
          >
            איפוס לסינון הלקוח
          </Button>
        </Tooltip>
      )}

      <Popover
        open={openChip !== null}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 2, width: 300, maxWidth: '90vw' } }}
      >
        {openChip === 'price' && (
          <PriceEditor filters={filters} onChange={onChange} onClose={closePopover} />
        )}
        {openChip === 'rooms' && <RoomsEditor filters={filters} onChange={onChange} />}
        {openChip === 'size' && (
          <SizeEditor filters={filters} onChange={onChange} onClose={closePopover} />
        )}
        {openChip === 'locations' && <LocationsEditor filters={filters} onChange={onChange} />}
        {openChip === 'types' && <TypesEditor filters={filters} onChange={onChange} />}
        {openChip === 'more' && <MoreEditor filters={filters} onChange={onChange} />}
      </Popover>
    </Stack>
  );
}

// ── popover editors ─────────────────────────────────────────────────────────────

function PriceEditor({
  filters,
  onChange,
  onClose
}: {
  filters: MatchFilters;
  onChange: (patch: Partial<MatchFilters>) => void;
  onClose: () => void;
}): JSX.Element {
  const [minText, setMinText] = useState(filters.minPrice ? String(filters.minPrice) : '');
  const [maxText, setMaxText] = useState(filters.maxPrice ? String(filters.maxPrice) : '');
  const [includeWithoutPrice, setIncludeWithoutPrice] = useState(
    Boolean(filters.includeListingsWithoutPrice)
  );

  const parse = (text: string): number | undefined => {
    const n = parseInt(text.replace(/[^\d]/g, ''), 10);
    return isFinite(n) && n > 0 ? n : undefined;
  };

  const apply = (): void => {
    onChange({
      minPrice: parse(minText),
      maxPrice: parse(maxText),
      includeListingsWithoutPrice: includeWithoutPrice
    });
    onClose();
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">טווח מחיר</Typography>
      {/* CurrencyInput (react-number-format) live-formats thousands and keeps
          the caret sane on type/paste/backspace — the same control the listings
          drawer's PriceRangePicker uses, so both price surfaces read alike.
          It hands back the FORMATTED string; `parse` strips non-digits, so the
          value reaching MatchFilters stays a plain number. */}
      <Stack direction="row" spacing={1}>
        <CurrencyInput
          size="small"
          label="מ־"
          value={minText}
          onChange={(e) => setMinText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
          inputProps={{ inputMode: 'numeric', dir: 'ltr' }}
        />
        <CurrencyInput
          size="small"
          label="עד"
          value={maxText}
          onChange={(e) => setMaxText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
          inputProps={{ inputMode: 'numeric', dir: 'ltr' }}
        />
      </Stack>
      {/* Yad2 publishes "מחיר לא צוין" listings as price 0, so a budget hides them —
          which is what filtering by budget means. This is the opt-in that brings them
          back ALONGSIDE the range, for the realtor who would rather see them and ask.
          It does nothing without a budget, hence the disabled state. */}
      <FormControlLabel
        sx={{ mx: 0, '& .MuiTypography-root': { typography: 'body2' } }}
        control={
          <Checkbox
            size="small"
            disabled={!parse(minText) && !parse(maxText)}
            checked={includeWithoutPrice}
            onChange={(e) => setIncludeWithoutPrice(e.target.checked)}
          />
        }
        label="כולל נכסים ללא מחיר"
      />
      <Stack direction="row" justifyContent="space-between">
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            setMinText('');
            setMaxText('');
            setIncludeWithoutPrice(false);
            onChange({
              minPrice: undefined,
              maxPrice: undefined,
              includeListingsWithoutPrice: false
            });
            onClose();
          }}
        >
          נקה
        </Button>
        <Button size="small" variant="contained" onClick={apply}>
          החל
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * The chip row applies INSTANTLY — one change, one search. That was a fair
 * trade while the range editors were dropdowns (one interaction = one final value),
 * but a +/− stepper turns "3 to 5 rooms" into four taps, i.e. four searches. This
 * collapses a burst of taps into a single apply. The stepper itself still reacts
 * immediately — it owns its own state; only the search (and the chip label) waits.
 *
 * Same precedent as ContactTableFilterRooms, which debounces RoomRangePicker.
 *
 * Deliberately NOT cancelled on unmount: the editors unmount the moment the popover
 * closes, so cancelling would silently swallow the last tap. MatchFilterChips
 * outlives them, which makes the late call safe.
 */
const STEPPER_APPLY_DEBOUNCE_MS = 300;

/** `clear()` is what lets an immediate action (נקה) win over a pending tap. */
type DebouncedPatch = ((patch: Partial<MatchFilters>) => void) & { clear: () => void };

function useDebouncedPatch(onChange: (patch: Partial<MatchFilters>) => void): DebouncedPatch {
  const latest = useRef(onChange);
  latest.current = onChange;

  return useMemo(
    () =>
      debounce(
        (patch: Partial<MatchFilters>) => latest.current(patch),
        STEPPER_APPLY_DEBOUNCE_MS
      ),
    []
  );
}

function RoomsEditor({
  filters,
  onChange
}: {
  filters: MatchFilters;
  onChange: (patch: Partial<MatchFilters>) => void;
}): JSX.Element {
  const applyDebounced = useDebouncedPatch(onChange);
  // RoomRangePicker seeds its state from initialMin/initialMax on MOUNT only, so
  // "נקה" has to remount it — otherwise the filters would clear while the stepper
  // kept showing the old numbers.
  const [seed, setSeed] = useState(0);

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">חדרים</Typography>
      {/* The same control as the full filters drawer and the contacts table's
          rooms filter — and, unlike the two dropdowns this replaced, it cannot be
          inverted (raising min pushes max, lowering max pulls min). */}
      <RoomRangePicker
        key={seed}
        initialMin={filters.minRooms}
        initialMax={filters.maxRooms}
        onRangeChange={(min, max) =>
          applyDebounced({ minRooms: min as Room, maxRooms: max as Room })
        }
      />
      <Button
        size="small"
        color="inherit"
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => {
          // Drop any tap still in flight, or it would re-apply the old range
          // ~300ms after the clear.
          applyDebounced.clear();
          setSeed((value) => value + 1);
          onChange({ minRooms: undefined, maxRooms: undefined });
        }}
      >
        נקה
      </Button>
    </Stack>
  );
}

/**
 * Built area, typed. Applied on "החל" (or Enter) like the price editor and unlike the steppers:
 * a typed number passes through every prefix of itself, and searching on "1", "10" and "100" in
 * turn is three wasted requests and a result list that jumps under the realtor's hands.
 */
function SizeEditor({
  filters,
  onChange,
  onClose
}: {
  filters: MatchFilters;
  onChange: (patch: Partial<MatchFilters>) => void;
  onClose: () => void;
}): JSX.Element {
  const [range, setRange] = useState<{ min?: number; max?: number }>({
    min: filters.minSquareMeter,
    max: filters.maxSquareMeter
  });

  const apply = (): void => {
    // Enter can arrive before the control's own blur swaps an inverted pair.
    const inverted = range.min !== undefined && range.max !== undefined && range.min > range.max;
    onChange({
      minSquareMeter: inverted ? range.max : range.min,
      maxSquareMeter: inverted ? range.min : range.max
    });
    onClose();
  };

  return (
    <Stack spacing={1.5} onKeyDown={(event) => event.key === 'Enter' && apply()}>
      <Typography variant="subtitle2">שטח בנוי</Typography>
      <SizeRangePicker
        autoFocus
        initialMin={filters.minSquareMeter}
        initialMax={filters.maxSquareMeter}
        onRangeChange={(min, max) => setRange({ min, max })}
        hint="אפשר למלא רק מינימום. נכסים שלא צוין בהם שטח לא יוצגו."
      />
      <Stack direction="row" justifyContent="space-between">
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            onChange({ minSquareMeter: undefined, maxSquareMeter: undefined });
            onClose();
          }}
        >
          נקה
        </Button>
        <Button size="small" variant="contained" onClick={apply}>
          החל
        </Button>
      </Stack>
    </Stack>
  );
}

function LocationsEditor({
  filters,
  onChange
}: {
  filters: MatchFilters;
  onChange: (patch: Partial<MatchFilters>) => void;
}): JSX.Element {
  const api = useWebotServer();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<ProcessedListingAddress[]>([]);
  const [searching, setSearching] = useState(false);
  const requestSeq = useRef(0);

  // Suggestions come from the canonical location registry, not Algolia: since the
  // Postgres cutover there is no browser-side index at all, and this editor silently
  // returned an empty list on every keystroke. The registry is also the better
  // source — picking a suggestion carries its canonical id, which matches alternate
  // spellings and child neighborhoods of the same place rather than one raw string.
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      setSearching(false);
      return;
    }
    const seq = ++requestSeq.current;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        // includeEmpty stays FALSE here: this picker narrows a listing SEARCH, so a
        // registry entry holding no listings today would be a dead option that
        // returns nothing when picked. (The alerts picker passes true — there, an
        // empty area is a legitimate thing to watch.)
        const { suggestions: found } = await api.locationsAutocomplete(query.trim(), {
          resultType: 'any'
        });
        if (seq !== requestSeq.current) return;
        setSuggestions(found.slice(0, 8).map(locationSuggestionToAddress));
      } catch (err) {
        console.error('MatchesTab: location autocomplete failed', err);
        if (seq === requestSeq.current) setSuggestions([]);
      } finally {
        if (seq === requestSeq.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [api, query]);

  const locations = filters.locations ?? [];
  const isSelected = (s: ProcessedListingAddress): boolean =>
    locations.some(
      (l) =>
        (l.city ?? '') === (s.city ?? '') &&
        (l.neighborhood ?? '') === (s.neighborhood ?? '') &&
        (l.street ?? '') === (s.street ?? '')
    );

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">אזורים</Typography>
      {locations.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {locations.map((location, i) => (
            <Button
              key={`${locationLabel(location)}-${i}`}
              size="small"
              color="inherit"
              endIcon={<Close sx={{ fontSize: '14px !important' }} />}
              onClick={() => onChange({ locations: locations.filter((_l, j) => j !== i) })}
              sx={{
                borderRadius: 999,
                px: 1,
                py: 0,
                minHeight: 26,
                typography: 'caption',
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12)
              }}
            >
              {locationLabel(location)}
            </Button>
          ))}
        </Stack>
      )}
      <TextField
        size="small"
        autoFocus
        placeholder="חיפוש עיר / שכונה / רחוב"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          endAdornment: searching ? <CircularProgress size={14} /> : undefined
        }}
      />
      <Stack spacing={0} sx={{ maxHeight: 200, overflowY: 'auto' }}>
        {suggestions.map((suggestion, i) => {
          const selected = isSelected(suggestion);
          return (
            <Button
              key={`${locationLabel(suggestion)}-${i}`}
              size="small"
              color="inherit"
              disabled={selected}
              onClick={() => {
                onChange({ locations: [...locations, suggestion] });
                setQuery('');
                setSuggestions([]);
              }}
              sx={{ justifyContent: 'flex-start', typography: 'body2' }}
            >
              {[suggestion.street, suggestion.neighborhood, suggestion.city]
                .filter(Boolean)
                .join(', ')}
              {selected ? ' ✓' : ''}
            </Button>
          );
        })}
        {!searching && query.trim() && suggestions.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
            לא נמצאו אזורים תואמים
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function TypesEditor({
  filters,
  onChange
}: {
  filters: MatchFilters;
  onChange: (patch: Partial<MatchFilters>) => void;
}): JSX.Element {
  const selected = filters.propertyTypes ?? [];

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">סוג נכס</Typography>
        {selected.length > 0 && (
          <Button size="small" color="inherit" onClick={() => onChange({ propertyTypes: [] })}>
            נקה
          </Button>
        )}
      </Stack>
      {/* Sectioned (דירות / צמודי קרקע / מסחרי / אחר) by the SAME
          groupPropertyTypes() taxonomy the drawer's PropertyTypeSelect uses, so
          the two surfaces can never disagree about where a type lives. A Select
          would be the wrong control here — its portalled menu inside a 300px
          Popover fights the outer click-away — hence the checkbox variant. */}
      <PropertyTypeCheckboxList
        options={allPropertyTypes}
        value={selected}
        onChange={(propertyTypes) => onChange({ propertyTypes })}
      />
    </Stack>
  );
}

function MoreEditor({
  filters,
  onChange
}: {
  filters: MatchFilters;
  onChange: (patch: Partial<MatchFilters>) => void;
}): JSX.Element {
  const applyDebounced = useDebouncedPatch(onChange);
  const booleanProps = filters.booleanProps ?? {};
  const shelterActive = hasAnyProtectedSpace(booleanProps as Record<string, boolean | undefined>);
  const toggleAmenity = (key: string): void => {
    const next: Partial<GenericPropertyDetails> = { ...booleanProps };
    if ((next as Record<string, boolean | undefined>)[key]) {
      delete (next as Record<string, boolean | undefined>)[key];
    } else {
      (next as Record<string, boolean>)[key] = true;
    }
    onChange({ booleanProps: next });
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">סינון נוסף</Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={filters.listedFor}
        onChange={(_e, value) => value && onChange({ listedFor: value })}
        sx={{ width: '100%', '& .MuiToggleButton-root': { flex: 1 } }}
      >
        <ToggleButton value="sale">למכירה</ToggleButton>
        <ToggleButton value="rent">להשכרה</ToggleButton>
      </ToggleButtonGroup>

      <Divider />

      <Typography variant="caption" color="text.secondary">
        קומות
      </Typography>
      {/* Same stepper as the full filters drawer. The shared `floors` scale is
          contiguous, so each tap moves exactly one floor. */}
      <FloorRangePicker
        initialMin={filters.minFloors}
        initialMax={filters.maxFloors}
        onRangeChange={(min, max) =>
          applyDebounced({ minFloors: min as Floor, maxFloors: max as Floor })
        }
      />

      <Divider />

      <Typography variant="caption" color="text.secondary">
        מאפיינים
      </Typography>
      {/* The מרחב מוגן group is boxed and ORed server-side (`amenities && …`); everything else is
          ANDed. Ticking ממ״ד + מקלט here WIDENS the result — before 2026-09-22 it narrowed it to
          nothing, because the three were ANDed and no listing carries all of them. */}
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          border: 1,
          borderColor: shelterActive ? 'primary.main' : 'divider',
          bgcolor: (theme) => (shelterActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent')
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
          {PROTECTED_SPACE_GROUP_LABEL}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5 }}>
          {PROTECTED_SPACE_KEYS.map((key) => (
            <FormControlLabel
              key={key}
              sx={{ mx: 0, '& .MuiTypography-root': { typography: 'body2' } }}
              control={
                <Checkbox
                  size="small"
                  checked={Boolean((booleanProps as Record<string, boolean | undefined>)[key])}
                  onChange={() => toggleAmenity(key)}
                />
              }
              label={AMENITY_LABELS[key] ?? key}
            />
          ))}
        </Box>
        {shelterActive && (
          <Typography variant="caption" sx={{ display: 'block', color: 'primary.dark' }}>
            {PROTECTED_SPACE_GROUP_HINT}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 0 }}>
        {STANDALONE_FILTER_KEYS.map((key) => (
          <FormControlLabel
            key={key}
            sx={{ mx: 0, '& .MuiTypography-root': { typography: 'body2' } }}
            control={
              <Checkbox
                size="small"
                checked={Boolean((booleanProps as Record<string, boolean | undefined>)[key])}
                onChange={() => toggleAmenity(key)}
              />
            }
            label={AMENITY_LABELS[key] ?? key}
          />
        ))}
      </Box>
    </Stack>
  );
}
